import { useEffect, useState } from "react";
import { MapPin, Menu as MenuIcon, X } from "lucide-react";
import { IMG, LINKS, NAV } from "../data/content";
import { CONVERSIONS, reportConversion } from "../utils/gtag";

export const scrollTo = (e, href) => {
  e.preventDefault();
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "border-b border-neon/15 bg-espresso/85 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        <a
          href="#top"
          data-testid="header-logo"
          onClick={(e) => scrollTo(e, "#top")}
          className="group flex items-center gap-3"
        >
          <img
            src={IMG.logo}
            alt="Brewing Bistro logo"
            className="h-10 w-10 rounded-full ring-1 ring-neon/40 transition-transform duration-500 group-hover:rotate-[360deg]"
          />
          <span className="font-serif text-lg tracking-wide text-cream">
            Brewing <span className="italic text-neon">Bistro</span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={n.testid}
              onClick={(e) => scrollTo(e, n.href)}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-sage transition-colors duration-300 hover:text-neon"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={LINKS.maps}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-directions-button"
            onClick={() => reportConversion(CONVERSIONS.directions)}
            className="group hidden items-center gap-2 rounded-full bg-neon px-5 py-2.5 text-sm font-medium text-espresso transition-transform duration-300 hover:scale-105 sm:flex"
          >
            <MapPin size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5" />
            Get Directions
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-cream md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-neon/10 bg-espresso/95 px-6 py-4 backdrop-blur-xl md:hidden">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`${n.testid}-mobile`}
              onClick={(e) => {
                setOpen(false);
                scrollTo(e, n.href);
              }}
              className="block py-3 font-mono text-xs uppercase tracking-[0.22em] text-sage"
            >
              {n.label}
            </a>
          ))}
          <a
            href={LINKS.maps}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-directions-button-mobile"
            onClick={() => reportConversion(CONVERSIONS.directions)}
            className="mt-2 flex items-center justify-center gap-2 rounded-full bg-neon px-5 py-3 text-sm font-medium text-espresso"
          >
            <MapPin size={15} /> Get Directions
          </a>
        </nav>
      )}
    </header>
  );
}
